// demo 的「操作 ▾」下拉：打开 2D 回放 / 计分板 / 数据分析、下载原始 demo、编辑标题与标签、重新解析、删除。
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { BarChart3, ChartColumn, Download, Pencil, Play, RefreshCw, Trash2 } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm";
import { DialogContent, DialogRoot } from "@/components/ui/dialog";
import { Field } from "@/components/ui/input";
import { ActionsMenu, MenuItem, MenuSep } from "@/components/ui/menu";
import { errorMessage } from "@/lib/api/client";
import { ep, qk } from "@/lib/api/endpoints";
import type { Demo } from "@/lib/api/types";
import { demoTitle } from "@/lib/format";

export function useDemoMutations(demo: Demo) {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: qk.demosAll });
  };
  const reparse = useMutation({
    mutationFn: () => ep.reparseDemo(demo.id),
    onSuccess: () => {
      toast.success("已重新加入解析队列");
      invalidate();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: () => ep.deleteDemo(demo.id),
    onSuccess: () => {
      toast.success("demo 已删除");
      qc.removeQueries({ queryKey: qk.demo(demo.id) });
      invalidate();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const download = async () => {
    try {
      const ps = await ep.demoDownload(demo.id);
      window.location.assign(ps.url);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  return { reparse, remove, download };
}

export function EditDemoDialog({ demo, open, onOpenChange }: { demo: Demo; open: boolean; onOpenChange: (o: boolean) => void }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState(demo.title ?? "");
  const [tags, setTags] = useState(demo.tags.join("，"));
  const [notes, setNotes] = useState(demo.notes ?? "");
  const save = useMutation({
    mutationFn: () =>
      ep.updateDemo(demo.id, {
        title: title.trim(),
        notes: notes.trim(),
        tags: tags
          .split(/[,，\s]+/)
          .map((t) => t.trim())
          .filter(Boolean),
      }),
    onSuccess: () => {
      toast.success("已保存");
      qc.invalidateQueries({ queryKey: qk.demosAll });
      onOpenChange(false);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title="编辑标题与标签"
        maxWidth={560}
        footer={
          <>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              取消
            </Button>
            <Button variant="default" disabled={save.isPending} onClick={() => save.mutate()}>
              保存
            </Button>
          </>
        }
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <Field label="标题" value={title} maxLength={120} placeholder={demo.original_filename} onChange={(e) => setTitle(e.target.value)} />
          <Field label="标签" value={tags} hint="用逗号或空格分隔，最多 20 个" onChange={(e) => setTags(e.target.value)} />
          <div className="field">
            <label className="cs-label" htmlFor="demo-notes">
              备注
            </label>
            <textarea id="demo-notes" className="textarea" maxLength={4000} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <button type="submit" className="sr-only" tabIndex={-1} aria-hidden="true" />
        </form>
      </DialogContent>
    </DialogRoot>
  );
}

/** demo 操作下拉（页面头部或播放栏）。navigation 项会隐藏当前所在页。 */
export function DemoActionsMenu({ demo, trigger, side, align, navigation = true, scoreboard = true }: {
  demo: Demo;
  trigger?: ReactNode;
  side?: "top" | "bottom" | "right";
  align?: "start" | "end";
  /** false：不显示跳转项（播放栏里已有计分板 / 回合按钮）。 */
  navigation?: boolean;
  /** false：跳转项里不含计分板（Demo 库行内菜单）。 */
  scoreboard?: boolean;
}) {
  const navigate = useNavigate();
  const loc = useLocation();
  const confirm = useConfirm();
  const [editing, setEditing] = useState(false);
  const { reparse, remove, download } = useDemoMutations(demo);
  const base = `/demos/${demo.id}`;
  const ready = demo.status === "ready";
  const nav = ready && navigation;
  const here = loc.pathname.replace(/\/$/, "");
  return (
    <>
      <ActionsMenu trigger={trigger} side={side} align={align}>
        {nav && here !== base ? (
          <MenuItem icon={<Play className="ico" />} onSelect={() => navigate(base)}>
            打开 2D 回放
          </MenuItem>
        ) : null}
        {nav && scoreboard && here !== base ? (
          <MenuItem icon={<BarChart3 className="ico" />} onSelect={() => navigate(`${base}?scoreboard=1`)}>
            计分板
          </MenuItem>
        ) : null}
        {nav && here !== "/analysis/history" ? (
          <MenuItem icon={<ChartColumn className="ico" />} onSelect={() => navigate(`/analysis/history?demo=${demo.id}`)}>
            数据分析
          </MenuItem>
        ) : null}
        {nav ? <MenuSep /> : null}
        <MenuItem icon={<Download className="ico" />} disabled={demo.status === "pending_upload" || demo.visibility === "restricted"} onSelect={download}>
          下载原始 demo{demo.visibility === "restricted" ? "（仅己方视野不提供）" : ""}
        </MenuItem>
        {demo.can_write ? (
          <>
            <MenuItem icon={<Pencil className="ico" />} onSelect={() => setEditing(true)}>
              编辑标题与标签
            </MenuItem>
            <MenuItem
              icon={<RefreshCw className="ico" />}
              disabled={reparse.isPending || demo.status === "pending_upload" || demo.status === "parsing" || demo.status === "queued"}
              onSelect={() => reparse.mutate()}
            >
              重新解析
            </MenuItem>
            <MenuSep />
            <MenuItem
              danger
              icon={<Trash2 className="ico" />}
              onSelect={async () => {
                const ok = await confirm({
                  title: "删除 demo？",
                  description: `「${demoTitle(demo)}」的原始文件、解析结果与 2D 回放数据都会被删除，不可恢复。`,
                  confirmText: "删除",
                  destructive: true,
                });
                if (ok) {
                  await remove.mutateAsync();
                  // 在 demo 自身页面删除时回到库；在库里删除则留在原地（保留筛选）。
                  if (here === base || here.startsWith(`${base}/`)) navigate("/demos", { replace: true });
                }
              }}
            >
              删除 demo
            </MenuItem>
          </>
        ) : null}
      </ActionsMenu>
      {editing ? <EditDemoDialog demo={demo} open={editing} onOpenChange={setEditing} /> : null}
    </>
  );
}
